import React, { useEffect, useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
} from 'react-router-dom';
import { CartProvider, useCart } from './context/CartContext';
import { LocaleProvider } from './i18n';
import ErrorBoundary from './components/ErrorBoundary';
import MenuPage from './pages/MenuPage';
import CartPage from './pages/CartPage';
import OrderStatusPage from './pages/OrderStatusPage';
import PaymentPage from './pages/PaymentPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentFailPage from './pages/PaymentFailPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import SupportPage from './pages/SupportPage';
import RefundRequestPage from './pages/RefundRequestPage';
import CashReceiptPage from './pages/CashReceiptPage';
import OrderHistoryPage from './pages/OrderHistoryPage';

// Table route wrapper: extracts storeId + tableId from URL and sets in context
function TableRouteWrapper({ children }) {
  const { storeId, tableId } = useParams();
  const { setSessionInfo } = useCart();

  useEffect(() => {
    if (storeId && tableId) {
      // Only restore a real UUID session (not fake generated ones)
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const stored = localStorage.getItem('sessionId');
      const sessionId = (stored && uuidPattern.test(stored)) ? stored : null;
      setSessionInfo({ sessionId, tableId, storeId });
    }
  }, [storeId, tableId, setSessionInfo]);

  return children;
}

// Landing page shown when scanning QR without params
function LandingPage() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'var(--bg)',
        textAlign: 'center',
        gap: '16px',
      }}
    >
      <div
        className="qr-float"
        style={{
          width: 80,
          height: 80,
          borderRadius: 20,
          background: 'var(--primary-light)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 8,
        }}
      >
        <svg width="40" height="40" viewBox="0 0 56 56" fill="none">
          <rect x="4" y="4" width="20" height="20" rx="3" stroke="var(--primary)" strokeWidth="2.5" fill="none" />
          <rect x="9" y="9" width="10" height="10" rx="1" fill="var(--primary)" />
          <rect x="32" y="4" width="20" height="20" rx="3" stroke="var(--primary)" strokeWidth="2.5" fill="none" />
          <rect x="37" y="9" width="10" height="10" rx="1" fill="var(--primary)" />
          <rect x="4" y="32" width="20" height="20" rx="3" stroke="var(--primary)" strokeWidth="2.5" fill="none" />
          <rect x="9" y="37" width="10" height="10" rx="1" fill="var(--primary)" />
          <rect x="32" y="32" width="6" height="6" rx="1" fill="var(--primary)" />
          <rect x="32" y="44" width="6" height="8" rx="1" fill="var(--primary)" />
          <rect x="44" y="32" width="8" height="6" rx="1" fill="var(--primary)" />
          <rect x="44" y="44" width="8" height="8" rx="1" fill="var(--primary)" />
        </svg>
      </div>
      <h1 className="animate-fade-in-up" style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.5px', animationDelay: '50ms' }}>
        스마트 오더
      </h1>
      <p className="animate-fade-in-up" style={{ fontSize: 15, color: 'var(--text-tertiary)', lineHeight: 1.7, animationDelay: '100ms' }}>
        테이블에 있는 QR 코드를 스캔하면<br />바로 주문을 시작할 수 있어요
      </p>
      <div
        className="stagger-children"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          marginTop: 20,
          width: '100%',
          maxWidth: 300,
        }}
      >
        {[
          { num: '1', text: 'QR 코드 스캔' },
          { num: '2', text: '메뉴 선택 및 주문' },
          { num: '3', text: '셀프 결제' },
        ].map(({ num, text }, idx) => (
          <div
            key={num}
            className="animate-fade-in-up"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '14px 18px',
              background: 'var(--border)',
              borderRadius: 12,
              animationDelay: `${(idx + 1) * 100}ms`,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'var(--primary)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              {num}
            </div>
            <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)' }}>{text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/table/:storeId/:tableId"
        element={
          <TableRouteWrapper>
            <MenuPage />
          </TableRouteWrapper>
        }
      />
      <Route
        path="/cart/:storeId/:tableId"
        element={
          <TableRouteWrapper>
            <CartPage />
          </TableRouteWrapper>
        }
      />
      <Route path="/orders/:sessionId" element={<OrderStatusPage />} />
      <Route
        path="/history/:storeId/:tableId"
        element={
          <TableRouteWrapper>
            <OrderHistoryPage />
          </TableRouteWrapper>
        }
      />
      <Route path="/payment/:sessionId" element={<PaymentPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/fail" element={<PaymentFailPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/refund-request/:sessionId" element={<RefundRequestPage />} />
      <Route path="/cash-receipt/:sessionId" element={<CashReceiptPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <ErrorBoundary>
      <LocaleProvider>
      <BrowserRouter>
        <CartProvider>
          <div className="app-wrapper">
            {isOffline && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100%',
                  maxWidth: 480,
                  background: 'var(--danger)',
                  color: '#fff',
                  textAlign: 'center',
                  padding: '8px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  zIndex: 10000,
                }}
              >
                인터넷 연결이 끊어졌습니다
              </div>
            )}
            <AppRoutes />
          </div>
        </CartProvider>
      </BrowserRouter>
      </LocaleProvider>
    </ErrorBoundary>
  );
}

export default App;
